import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalSize, NotificationToaster } from "components/notifications";
import PropTypes from "prop-types";
import { GridLayout } from "components/grid";
import { DataDisplayRecord } from "components/data-display";
import { Button, ButtonSave } from "components/buttons";
import { Numeric } from "components/formatting";
import { LicenceCheckingService } from "services";
import { ShowHide } from "components/layout";
import { Icon, Intent, Spinner } from "@blueprintjs/core";
import classNames from "classnames";

export function LicenceCheckingManualCheckModal({
    isOpen,
    onClose,
    onCheckSuccess,
    credits = 0,
    employeeName = '',
    employeeId = null
}) {

    const [attemptingCheck, setAttemptingCheck] = useState(false);
    const [checkInProgress, setCheckInProgress] = useState(false);
    const [modalTitle, setModalTitle] = useState("Perform Licence Check");
    const [checkStatus, setCheckStatus] = useState("");
    const [queueId, setQueueId] = useState(null);
    const [checkCompleted, setCheckCompleted] = useState(false);
    const [fetchingStatus, setFetchingStatus] = useState(false);

    const progressInterval = useRef(null);

    useEffect(() => {
        return function () {
            if (progressInterval.current) {
                clearInterval(progressInterval.current);
            }
        };
    }, []);

    function onPerformCheckClick() {

        if (employeeId == null) {
            return;
        }

        setAttemptingCheck(true);

        LicenceCheckingService.performManualCheck(employeeId).then((response) => {
            setCheckInProgress(true);
            setModalTitle("Performing Check");
            setCheckStatus("Queued");
            setQueueId(response);
        }, function (error) {
            const validationError = 400;
            const errorMessage = error.response && error.response.status === validationError ? error.response.data : "An error occurred while performing the check. Please try again later.";

            NotificationToaster.show(Intent.DANGER, errorMessage);
        }).finally(() => {
            setAttemptingCheck(false);
        });

    }

    useEffect(() => {

        if (queueId == null) {
            return;
        }

        const checkTimeDuration = 5000; // 5 seconds

        //clear out any old intervals
        if (progressInterval.current) {
            clearInterval(progressInterval.current);
        }

        progressInterval.current = setInterval(() => {

            if (!checkInProgress) {
                return;
            }

            setFetchingStatus(true);

            LicenceCheckingService.checkManualCheckProgress(queueId)
                .then(function (response) {
                    switch (response.status) {
                        case "QUEUED":
                            setCheckStatus("Queued");
                            break;
                        case "PROCESSING":
                            setCheckStatus("In Progress");
                            break;
                        case "ERRORED":
                            setCheckStatus("The check could not be completed at this time, you have not been charged");
                            break;
                        case "DONE":
                            setCheckStatus("Check has been completed successfully");
                            clearInterval(progressInterval.current);
                            setCheckCompleted(true);
                            break;
                    }
                }, function () {
                    setCheckStatus("Unknown");
                }).finally(function () {

                    //Give a visual indication that something has changed
                    const statusUpdateTimeout = 500;
                    setTimeout(function () {
                        setFetchingStatus(false);
                    }, statusUpdateTimeout);
                });
        }, checkTimeDuration);

    }, [queueId]);


    function onModalClose() {
        if (progressInterval.current) {
            clearInterval(progressInterval.current);
        }

        if (checkCompleted) {
            onCheckSuccess();
            setCheckInProgress(false);
        }

        onClose();
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onModalClose}
            title={modalTitle}
            size={ModalSize.SMALL}
            clickOutsideClose={false}
        >
            <ShowHide
                evaluator={checkInProgress}
                show={(
                    <>
                        <div className="spacer-bottom-small">
                            <div className="spacer-bottom">
                                <p>The manual check is now in progress and once completed a credit will be charged. If you close this window the check will still happen.</p>
                            </div>
                            <div className="inline-items">
                                <ShowHide
                                    evaluator={checkCompleted}
                                    show={(
                                        <Icon
                                            icon="tick-circle"
                                            size={32}
                                            color="#00c6b2"
                                        />
                                    )}
                                    hide={(
                                        <Spinner className="spinner" size={Spinner.SIZE_STANDARD} intent={Intent.PRIMARY} />
                                    )}
                                />
                                <DataDisplayRecord
                                    header="Current Progress"
                                    data={<span className={classNames({ "bp6-skeleton": fetchingStatus })}>{checkStatus}</span>}
                                />
                            </div>
                        </div>
                        <div className="inline-items inline-items-end">
                            <Button
                                text={"Close"}
                                intent="secondary"
                                onClick={onModalClose}
                            />
                        </div>
                    </>
                )}
                hide={(
                    <div>
                        <div className="spacer-bottom-small">
                            <p>Are you sure you want to perform a manual check on {employeeName}?</p>
                        </div>
                        <div className="spacer-bottom">
                            <GridLayout numberOfColumns={2}>
                                <DataDisplayRecord
                                    header="Licence Checking Credits"
                                    data={<Numeric>{credits}</Numeric>}
                                />
                                <DataDisplayRecord
                                    header="Manual Check Cost"
                                    data={1}
                                />
                            </GridLayout>
                        </div>
                        <div className="inline-items inline-items-end">
                            <Button
                                text={"Cancel"}
                                intent="secondary"
                                onClick={onModalClose}
                            />
                            <ButtonSave
                                text={"Perform Check"}
                                intent="primary"
                                disabled={attemptingCheck}
                                onClick={onPerformCheckClick}
                                icon={null}
                            />
                        </div>
                    </div>
                )}
            />

        </Modal>
    );
}

LicenceCheckingManualCheckModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCheckSuccess: PropTypes.func.isRequired,
    credits: PropTypes.number,
    employeeName: PropTypes.string,
    employeeId: PropTypes.string,
};
